'use client';

import type { ComponentPropsWithRef } from 'react';
import {
    Root as DialogRoot,
    Trigger as DialogTrigger,
    Portal as DialogPortal,
    Overlay as DialogOverlay,
    Content as DialogContent,
    Title as DialogTitle,
    Close as DialogClose,
} from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';
import { Icon } from '@/shared-ui/atoms/icon';
import { CircledClose } from '@/shared-ui/icons/circled-close';

type RootProps = ComponentPropsWithRef<typeof DialogRoot>;
type TriggerProps = ComponentPropsWithRef<typeof DialogTrigger>;
type PortalProps = ComponentPropsWithRef<typeof DialogPortal>;
type OverlayProps = ComponentPropsWithRef<typeof DialogOverlay>;
type ContentProps = ComponentPropsWithRef<typeof DialogContent>;
type TitleProps = ComponentPropsWithRef<typeof DialogTitle>;
type CloseProps = ComponentPropsWithRef<typeof DialogClose>;
type CloseButtonProps = ComponentPropsWithRef<'button'>;

const Root = (props: RootProps) => <DialogRoot data-slot="dialog-root" {...props} />;
Root.displayName = 'Dialog.Root';

const Trigger = (props: TriggerProps) => <DialogTrigger data-slot="dialog-trigger" {...props} />;
Trigger.displayName = 'Dialog.Trigger';

const Portal = (props: PortalProps) => <DialogPortal data-slot="dialog-portal" {...props} />;
Portal.displayName = 'Dialog.Portal';

const Overlay = ({ className, ...props }: OverlayProps) => (
    <DialogOverlay
        data-slot="dialog-overlay"
        className={cn('z-50 fixed inset-0 bg-primary/60', className)}
        {...props}
    />
);
Overlay.displayName = 'Dialog.Overlay';

const Content = ({ className, ...props }: ContentProps) => (
    <DialogContent
        data-slot="dialog-content"
        className={cn(
            'flex p-8 flex-col items-start fixed left-1/2 top-1/2 z-50 w-[90%] h-[calc(100%-120px)] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-snk-white',
            className
        )}
        {...props}
    />
);
Content.displayName = 'Dialog.Content';

const Title = (props: TitleProps) => <DialogTitle data-slot="dialog-title" {...props} />;
Title.displayName = 'Dialog.Title';

const Close = (props: CloseProps) => <DialogClose data-slot="dialog-close" {...props} />;
Close.displayName = 'Dialog.Close';

const CloseButton = ({ className, ...props }: CloseButtonProps) => {
    return (
        <DialogClose asChild>
            <button
                type="button"
                className={cn('absolute size-8 -top-[40px] right-0 cursor-pointer', className)}
                aria-label="Close"
                {...props}>
                <span aria-hidden="true" className="flex items-center justify-center">
                    <Icon icon={CircledClose} />
                </span>
            </button>
        </DialogClose>
    );
};
CloseButton.displayName = 'Dialog.CloseButton';

const Dialog = {
    Root,
    Trigger,
    Portal,
    Overlay,
    Content,
    Title,
    Close,
    CloseButton,
} as const;

export { Dialog };
